/**
 * Custom hook for managing table items in DrivePanel
 *
 * Extracted from DrivePanel.tsx
 */

import { useMemo } from 'react';
import type { SavedMessage, PDFSnippet } from '../../../services/api';
import type { SavedContent } from '../../../services/savedContentService';
import type { TableItem } from '../../../services/driveService';
import { transformToTableItems, getUniqueFilterValues, applyDriveFilters } from '../../../services/driveService';
import type {
  UseDriveTableItemsProps,
  UseDriveTableItemsReturn,
} from '../types/drive-panel.types';

/**
 * Manages table items computation (filtering, sorting, unique values)
 */
export function useDriveTableItems({
  privateDocuments,
  sharedDocuments,
  savedMessages,
  snippets,
  studioResults,
  originalCompanyNames,
  originalPeriods,
  filterItemType,
  filterPeriod,
  filterDocType,
  filterCompany,
  debouncedSearchTerm,
  searchInContent,
  sortItems,
}: UseDriveTableItemsProps): UseDriveTableItemsReturn {
  // Obtener valores únicos para los filtros usando el servicio
  const { uniqueDocTypes, uniqueCompanies, uniquePeriods } = useMemo(() => {
    return getUniqueFilterValues(privateDocuments, sharedDocuments);
  }, [privateDocuments, sharedDocuments]);

  // Convertir documentos a formato de tabla usando el servicio
  const baseTableItems = useMemo<TableItem[]>(() => {
    return transformToTableItems(
      privateDocuments,
      sharedDocuments,
      savedMessages,
      snippets,
      studioResults,
      originalCompanyNames,
      originalPeriods
    );
  }, [privateDocuments, sharedDocuments, savedMessages, snippets, studioResults, originalCompanyNames, originalPeriods]);

  // Optimización: Separar filtering de sorting para evitar recalcular todo cuando solo cambia la selección
  // 1. Filtrar items (sin sorting) - solo se recalcula cuando cambian filtros o datos base
  const filteredItems = useMemo<TableItem[]>(() => {
    return applyDriveFilters(baseTableItems, {
      filterItemType,
      filterPeriod,
      filterDocType,
      filterCompany,
      debouncedSearchTerm,
      searchInContent,
    });
  }, [
    baseTableItems,
    filterItemType,
    filterPeriod,
    filterDocType,
    filterCompany,
    debouncedSearchTerm,
    searchInContent,
  ]);

  // 2. Ordenar items - solo se recalcula cuando cambia sort order
  const tableItems = useMemo<TableItem[]>(() => {
    return sortItems(filteredItems);
  }, [filteredItems, sortItems]);

  // Calcular número de filtros activos (simplificado)
  const activeFiltersCount = [
    filterItemType !== 'all',
    filterPeriod !== 'all',
    filterDocType !== 'all',
    filterCompany !== 'all' && (Array.isArray(filterCompany) ? filterCompany.length > 0 : true),
  ].filter(Boolean).length;

  return {
    uniqueDocTypes,
    uniqueCompanies,
    uniquePeriods,
    tableItems,
    activeFiltersCount,
  };
}
